/**
 * Type-Safe Event System
 * Provides strictly typed custom events for the toolkit
 */

import type { ShopifyVariant, ShopifyCart, ShopifyProduct } from './shopify';

export interface ToolkitEventMap {
  'variant:change': { variant: ShopifyVariant; product: ShopifyProduct };
  'variant:unavailable': { variant: ShopifyVariant; product: ShopifyProduct };
  'cart:update': { cart: ShopifyCart };
  'cart:add': { item: ShopifyCart['items'][0]; cart: ShopifyCart };
  'cart:error': { message: string; error?: Error };
  'drawer:open': { source: 'cart' | 'notification' | 'manual' };
  'drawer:close': Record<string, never>;
  'notify:submit': { email: string; variantId: number };
  'notify:success': { email: string; variantId: number };
  'notify:error': { message: string };
  'recommendations:load': { products: ShopifyProduct[]; source: string };
}

export type ToolkitEventType = keyof ToolkitEventMap;

export class ToolkitEvent<T extends ToolkitEventType> extends CustomEvent<
  ToolkitEventMap[T]
> {
  constructor(type: T, detail: ToolkitEventMap[T]) {
    super(type, { detail, bubbles: true, composed: true });
  }
}

/**
 * Type-safe event dispatcher
 */
export class EventBus {
  private target: EventTarget;

  constructor(target: EventTarget = document) {
    this.target = target;
  }

  /**
   * Dispatch a typed event
   */
  dispatch<T extends ToolkitEventType>(
    type: T,
    detail: ToolkitEventMap[T]
  ): void {
    const event = new ToolkitEvent(type, detail);
    this.target.dispatchEvent(event);
  }

  /**
   * Listen to a typed event
   */
  on<T extends ToolkitEventType>(
    type: T,
    callback: (event: ToolkitEvent<T>) => void,
    options?: AddEventListenerOptions
  ): () => void {
    const listener = callback as EventListener;
    this.target.addEventListener(type, listener, options);

    // Return unsubscribe function
    return () => this.target.removeEventListener(type, listener, options);
  }

  /**
   * Listen to an event once
   */
  once<T extends ToolkitEventType>(
    type: T,
    callback: (event: ToolkitEvent<T>) => void
  ): void {
    this.on(type, callback, { once: true });
  }

  /**
   * Remove all listeners for a specific event type
   */
  off<T extends ToolkitEventType>(type: T): void {
    // Note: This requires keeping track of listeners, simplified for demo
    console.warn(`EventBus.off() called for ${type} - implement listener tracking for production`);
  }
}

// Global event bus instance
export const eventBus = new EventBus();
